import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { gartnerHypePhases, defaultGartnerPositions } from '../data/mockData';
import { ExternalLink } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Button } from '../components/ui/button';

// SVG Path for the Gartner Hype Cycle curve
const CURVE_PATH = "M 30,180 Q 70,180 90,155 Q 115,125 140,60 Q 165,10 190,35 Q 215,65 240,130 Q 270,195 320,210 Q 380,225 440,215 Q 500,205 560,195";

// Calculate point on the curve based on phase position
const getPointOnCurve = (phase) => {
  const phasePositions = {
    innovation_trigger: { x: 80, y: 165 },
    peak_expectations: { x: 165, y: 25 },
    trough_disillusionment: { x: 270, y: 205 },
    slope_enlightenment: { x: 400, y: 210 },
    plateau_productivity: { x: 530, y: 195 },
  };
  return phasePositions[phase] || { x: 300, y: 150 };
};

// Short phase labels for the chart
const phaseLabels = {
  innovation_trigger: { en: 'Innovation\nTrigger', nl: 'Innovatie\nTrigger' },
  peak_expectations: { en: 'Peak of\nExpectations', nl: 'Piek van\nVerwachtingen' },
  trough_disillusionment: { en: 'Trough of\nDisillusionment', nl: 'Dal van\nDesillusie' },
  slope_enlightenment: { en: 'Slope of\nEnlightenment', nl: 'Helling van\nVerlichting' },
  plateau_productivity: { en: 'Plateau of\nProductivity', nl: 'Plateau van\nProductiviteit' },
};

const GartnerHypeCycle = ({ savedUseCases = [], allUseCases = [] }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [hoveredUseCase, setHoveredUseCase] = useState(null);

  // Build positions from use cases data (using DB gartner_phase first, then fallback to defaults)
  const positions = useMemo(() => {
    const posMap = { ...defaultGartnerPositions };
    allUseCases.forEach(uc => {
      if (uc.gartner_phase) {
        posMap[uc.title] = {
          phase: uc.gartner_phase,
          maturityYears: uc.gartner_maturity || defaultGartnerPositions[uc.title]?.maturityYears || '2-5 years'
        };
      }
    });
    return posMap;
  }, [allUseCases]);

  // Get saved use case titles for highlighting
  const savedTitles = useMemo(() => {
    return new Set(savedUseCases.map(uc => uc.title));
  }, [savedUseCases]);

  // Group use cases by phase
  const useCasesByPhase = useMemo(() => {
    const grouped = {};
    gartnerHypePhases.forEach(phase => {
      grouped[phase.id] = [];
    });

    allUseCases.forEach(uc => {
      const position = positions[uc.title];
      if (position) {
        grouped[position.phase]?.push({
          ...uc,
          isSaved: savedTitles.has(uc.title),
          maturityYears: position.maturityYears,
        });
      }
    });

    return grouped;
  }, [allUseCases, positions, savedTitles]);

  // Calculate positions for dots, spreading them if multiple in same phase
  const getDotPositions = () => {
    const dots = [];
    
    Object.entries(useCasesByPhase).forEach(([phaseId, useCases]) => {
      const basePoint = getPointOnCurve(phaseId);
      
      useCases.forEach((uc, index) => {
        // Spread dots around the base point
        const angle = (index / useCases.length) * Math.PI * 2;
        const radius = useCases.length > 1 ? 20 : 0;
        dots.push({
          ...uc,
          x: basePoint.x + Math.cos(angle) * radius,
          y: basePoint.y + Math.sin(angle) * radius * 0.6,
          phase: phaseId,
        });
      });
    });

    return dots;
  };

  const dots = getDotPositions();

  const handleUseCaseClick = (useCaseId) => {
    navigate(`/use-cases/${useCaseId}`);
  };

  return (
    <TooltipProvider>
      <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="gartner-hype-cycle">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            {language === 'nl' ? 'Gartner Hype Cycle voor AI in HR' : 'Gartner Hype Cycle for AI in HR'}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'nl' 
              ? 'Klik op een stip om de use case te bekijken. Jouw opgeslagen use cases zijn blauw.' 
              : 'Click on a dot to view the use case. Your saved use cases are highlighted in blue.'}
          </p>
        </div>

        {/* SVG Visualization */}
        <div className="relative w-full overflow-x-auto">
          <svg viewBox="0 0 620 320" className="w-full min-w-[600px]" style={{ height: '380px' }}>
            {/* Background gradient */}
            <defs>
              <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#2563eb" stopOpacity="0.15" />
                <stop offset="35%" stopColor="#ef4444" stopOpacity="0.1" />
                <stop offset="50%" stopColor="#6366f1" stopOpacity="0.1" />
                <stop offset="75%" stopColor="#3b82f6" stopOpacity="0.1" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0.15" />
              </linearGradient>
            </defs>

            {/* Grid lines */}
            <line x1="30" y1="240" x2="590" y2="240" stroke="#e5e7eb" strokeWidth="1" />
            <line x1="30" y1="20" x2="30" y2="240" stroke="#e5e7eb" strokeWidth="1" />

            {/* Axis Labels */}
            <text x="310" y="310" textAnchor="middle" className="text-sm fill-gray-500 font-medium">
              {language === 'nl' ? 'Tijd →' : 'Time →'}
            </text>
            <text x="15" y="130" textAnchor="middle" transform="rotate(-90, 15, 130)" className="text-sm fill-gray-500 font-medium">
              {language === 'nl' ? 'Verwachtingen' : 'Expectations'}
            </text>

            {/* The Hype Cycle Curve - wider glow */}
            <path
              d={CURVE_PATH}
              fill="none"
              stroke="url(#curveGradient)"
              strokeWidth="50"
              strokeLinecap="round"
            />
            
            {/* The Hype Cycle Curve - main line */}
            <path
              d={CURVE_PATH}
              fill="none"
              stroke="#6366f1"
              strokeWidth="3"
              strokeLinecap="round"
            />

            {/* Phase Labels - positioned below the chart */}
            {gartnerHypePhases.map((phase) => {
              const point = getPointOnCurve(phase.id);
              const label = phaseLabels[phase.id];
              const lines = (language === 'nl' ? label.nl : label.en).split('\n');
              
              return (
                <g key={phase.id}>
                  {lines.map((line, i) => (
                    <text
                      key={`${phase.id}-${line}`}
                      x={point.x}
                      y={265 + i * 14}
                      textAnchor="middle"
                      className="text-[11px] fill-gray-600 font-medium"
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            })}

            {/* Use Case Dots */}
            {dots.map((dot, index) => (
              <g key={`${dot.id || dot.title}-${index}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <circle
                      cx={dot.x}
                      cy={dot.y}
                      r={dot.isSaved ? 12 : 8}
                      fill={dot.isSaved ? '#2563eb' : '#94a3b8'}
                      stroke={dot.isSaved ? '#1d4ed8' : '#64748b'}
                      strokeWidth={dot.isSaved ? 3 : 2}
                      className="cursor-pointer transition-all duration-200 hover:opacity-80"
                      style={{
                        filter: dot.isSaved ? 'drop-shadow(0 3px 6px rgba(249, 115, 22, 0.5))' : 'drop-shadow(0 2px 3px rgba(0,0,0,0.1))',
                      }}
                      onClick={() => handleUseCaseClick(dot.id)}
                      onMouseEnter={() => setHoveredUseCase(dot)}
                      onMouseLeave={() => setHoveredUseCase(null)}
                    />
                  </TooltipTrigger>
                  <TooltipContent 
                    side="top" 
                    className="max-w-sm p-0 bg-white shadow-2xl border border-gray-200 rounded-lg overflow-hidden"
                    sideOffset={8}
                  >
                    <div className="p-4 space-y-3">
                      {/* Title */}
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-semibold text-gray-900 text-base leading-tight">{dot.title}</h4>
                        {dot.isSaved && (
                          <span className="flex-shrink-0 text-blue-500 text-lg">★</span>
                        )}
                      </div>
                      
                      {/* Description */}
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {dot.shortDescription || dot.description?.substring(0, 120) + '...'}
                      </p>
                      
                      {/* Tags */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          dot.impact === 'high' ? 'bg-green-100 text-green-700' :
                          dot.impact === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {dot.impact === 'high' ? '🚀 High' : dot.impact === 'medium' ? '📈 Medium' : '📊 Low'} Impact
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 font-medium">
                          ⏱ {dot.maturityYears}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          dot.difficulty === 'low' ? 'bg-emerald-100 text-emerald-700' :
                          dot.difficulty === 'medium' ? 'bg-blue-100 text-blue-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {dot.difficulty === 'low' ? 'Easy' : dot.difficulty === 'medium' ? 'Medium' : 'Complex'}
                        </span>
                      </div>
                      
                      {/* CTA Button */}
                      <Button 
                        size="sm" 
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUseCaseClick(dot.id);
                        }}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {language === 'nl' ? 'Bekijk Use Case' : 'View Use Case'}
                      </Button>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </g>
            ))}
          </svg>
        </div>

        {/* Legend */}
        <div className="mt-6 flex items-center gap-8 justify-center">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-blue-500 border-2 border-blue-600 shadow-md" />
            <span className="text-sm text-gray-700 font-medium">
              {language === 'nl' ? 'Jouw opgeslagen use cases' : 'Your saved use cases'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-slate-400 border-2 border-slate-500" />
            <span className="text-sm text-gray-700 font-medium">
              {language === 'nl' ? 'Overige use cases' : 'Other use cases'}
            </span>
          </div>
        </div>

        {/* Phase Summary Cards */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="grid grid-cols-5 gap-3">
            {gartnerHypePhases.map((phase) => {
              const count = useCasesByPhase[phase.id]?.length || 0;
              const savedCount = useCasesByPhase[phase.id]?.filter(uc => uc.isSaved).length || 0;
              const label = phaseLabels[phase.id];
              
              return (
                <div 
                  key={phase.id} 
                  className="text-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="text-2xl font-bold text-indigo-600">{count}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(language === 'nl' ? label.nl : label.en).replace('\n', ' ')}
                  </div>
                  {savedCount > 0 && (
                    <div className="text-xs text-blue-500 font-medium mt-1">
                      {savedCount} {language === 'nl' ? 'opgeslagen' : 'saved'} ★
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default GartnerHypeCycle;
